// Pure adapters that turn an incident-detail API response into the rows the
// reusable AlertConditionPopup renders. Each domain emits rows keyed in its own
// language so the popup stays domain-agnostic and the org meta `viewConditions.data`
// keys match the API the surface actually consumes:
//   - BMS Faults  → fault*  keys (diagnosticsSeverityMeta)
//   - Platform/Smart Alerts → alert* keys (alertSeverityMeta)
// Keep these pure and exported so they can be unit-tested without rendering.

const buildSeverityNameByLevel = (incidentSeverityMeta = []) =>
    incidentSeverityMeta.reduce((acc, { severityLevel, severityName }) => {
        acc[String(severityLevel)] = severityName;
        return acc;
    }, {});

const buildLevelByName = (incidentSeverityMeta = []) =>
    incidentSeverityMeta.reduce((acc, { severityLevel, severityName }) => {
        if (severityName) acc[severityName.toLowerCase()] = severityLevel;
        return acc;
    }, {});

// BMS Faults. The native API fields (description, faultReason, faultDisplayCondition,
// severityLevelID) are passed straight through so org meta `dataIndex` can reference
// any of them; severityName is injected (resolved from the level). The faultDescription
// / faultCondition aliases keep older bmsAlertV2 org meta working unchanged.
export const buildBmsConditionRows = (incidentInfo = {}) => {
    const { diagnosticsSeverityMeta = [], incidentSeverityMeta = [] } = incidentInfo;
    const nameByLevel = buildSeverityNameByLevel(incidentSeverityMeta);
    return diagnosticsSeverityMeta.map((row = {}) => ({
        ...row,
        severityName: nameByLevel[String(row.severityLevelID)] ?? '—',
        faultDescription: row.description,
        faultCondition: row.faultDisplayCondition,
    }));
};

// Platform/Smart Alerts. Native alert* fields (alertDisplayCondition, alertCondition,
// description, alertReason) pass straight through so org meta `dataIndex` can reference
// any of them with zero further UI changes; severityName is taken from the entry or
// resolved from the level. No fault-shaped remapping — platform data stays alert-shaped.
export const buildPlatformConditionRows = (incidentInfo = {}) => {
    const { alertSeverityMeta = [], incidentSeverityMeta = [] } = incidentInfo;
    const nameByLevel = buildSeverityNameByLevel(incidentSeverityMeta);
    const levelByName = buildLevelByName(incidentSeverityMeta);
    return alertSeverityMeta.map((row = {}) => {
        const level = row.severityLevel ?? row.severityLevelID ?? levelByName[row.severityName?.toLowerCase()] ?? null;
        return {
            ...row,
            severityName: row.severityName ?? nameByLevel[String(level)] ?? '—',
        };
    });
};

// Convenience selector used by the shared analyse dialog.
export const getConditionRowBuilder = (isPlatformAlert) =>
    isPlatformAlert ? buildPlatformConditionRows : buildBmsConditionRows;
